import React from "react";
import { Link } from "react-router-dom";
import {
  GraduationCap, ShieldCheck, Truck, Wrench, Briefcase, Crown,
  Languages, QrCode, FileText, PlayCircle, CheckCircle2, ArrowRight,
} from "lucide-react";

/**
 * TrainingHubShowcase — section on DemoLanding that surfaces the
 * full-fledged training infrastructure of ForgedOps. Covers OSHA
 * outreach, equipment, safety, PM, and shop training paths with
 * progress tracking, bilingual chips, QR access, and video lessons.
 *
 * Pure visual — illustrates capabilities for prospects.
 */
const PATHS = [
  {
    icon: ShieldCheck,
    tag: "PATH 01",
    title: "OSHA Outreach",
    chip: "10 + 30 hour",
    blurb: "Authorized trainer-style modules covering construction, "
      + "subparts C/D/E/M/N/O/P/Q with completion certificates routed "
      + "to admin and crew profiles.",
    items: [
      ["10-hr Construction · EN", 100],
      ["10-hr Construcción · ES",  86],
      ["30-hr Construction · EN",  62],
      ["30-hr Construcción · ES",  41],
    ],
    accent: "#FF6A00",
  },
  {
    icon: Truck,
    tag: "PATH 02",
    title: "Equipment & Operator",
    chip: "Manufacturer-aligned",
    blurb: "Pre-Op walkaround, blind-spot awareness, ground guide "
      + "signals, and tag-out workflows — by class of equipment.",
    items: [
      ["Excavator (336)",         92],
      ["Dozer (D6)",              78],
      ["Motor Grader (672)",      54],
      ["Skid Steer (320)",        88],
      ["Trench Box · Spotter",    71],
    ],
    accent: "#FFD000",
  },
  {
    icon: Wrench,
    tag: "PATH 03",
    title: "Site Safety & Trade",
    chip: "OSHA 1926 + 1910",
    blurb: "Trench safety, fall protection, silica · Table 1, hot "
      + "work, traffic control, hand & power tools, lockout/tagout.",
    items: [
      ["Trench Safety · CP",       84],
      ["Fall Protection",          90],
      ["Silica Awareness · Table 1", 73],
      ["MOT / Traffic Control",    66],
      ["LOTO",                      59],
    ],
    accent: "#65DA76",
  },
  {
    icon: Briefcase,
    tag: "PATH 04",
    title: "PM & Field Leadership",
    chip: "Internal series",
    blurb: "Field reports, RFI discipline, change-order discipline, "
      + "production tracking, and trade-fitting for new PMs.",
    items: [
      ["Daily Report discipline",  100],
      ["RFI / change-order flow",   83],
      ["Production tracking",       69],
      ["Crew coordination",         77],
    ],
    accent: "#FF6A00",
  },
];

const FEATURE_BAND = [
  { icon: Languages,   label: "Bilingual EN / ES",  copy: "Per-user language toggle persists; lessons & quizzes mirror." },
  { icon: QrCode,      label: "QR access",           copy: "Scan to onboard, sign in, or jump straight to a lesson." },
  { icon: PlayCircle,  label: "Video + slides",      copy: "Short, jobsite-shot lessons; downloadable PDFs." },
  { icon: CheckCircle2, label: "Completion logs",     copy: "Auto-routed to crew profiles + admin records + insurance pack." },
];

export default function TrainingHubShowcase() {
  return (
    <section
      className="border-t"
      style={{ borderColor: "#1A1A1A", background: "#0B0B0B" }}
      data-testid="training-hub-showcase"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
        <div className="mb-8">
          <div className="font-mono text-[11px] uppercase tracking-[0.4em] mb-2" style={{ color: "#FF6A00" }}>
            07 · TRAINING THAT CREWS ACTUALLY FINISH
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight max-w-3xl">
            OSHA outreach, equipment, safety, leadership.{" "}
            <span style={{ color: "#FF6A00" }}>One platform, one record.</span>
          </h2>
          <p className="text-sm mt-3 max-w-2xl" style={{ color: "#BDBDBD" }}>
            Bilingual lessons. Per-crew progress. QR onboarding. Completions
            land back in the operator's profile and the company's
            audit-ready records bundle. Auto-routed, every time.
          </p>
        </div>

        {/* Feature band */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px mb-10" style={{ background: "#1A1A1A" }}>
          {FEATURE_BAND.map(({ icon: Icon, label, copy }) => (
            <div key={label} className="p-4 sm:p-5" style={{ background: "#0F0F0F" }}>
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-md mb-3" style={{ background: "rgba(255,106,0,0.12)", color: "#FF6A00", border: "1px solid rgba(255,106,0,0.3)" }}>
                <Icon className="w-4 h-4" />
              </span>
              <div className="font-display font-black text-white text-sm mb-1">{label}</div>
              <div className="text-xs leading-relaxed" style={{ color: "#BDBDBD" }}>{copy}</div>
            </div>
          ))}
        </div>

        {/* Path tiles */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px" style={{ background: "#1A1A1A" }}>
          {PATHS.map((p) => (
            <PathCard key={p.title} path={p} />
          ))}
        </div>

        {/* Footer band */}
        <div className="mt-10 p-5 rounded-lg flex items-center justify-between gap-5 flex-wrap" style={{ background: "#0F0F0F", border: "1px solid #1A1A1A" }}>
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-md" style={{ background: "rgba(255,106,0,0.12)", color: "#FF6A00", border: "1px solid rgba(255,106,0,0.3)" }}>
              <GraduationCap className="w-5 h-5" />
            </span>
            <div>
              <div className="font-display font-black text-white">Authored, branded, and routed for your company.</div>
              <div className="text-xs" style={{ color: "#BDBDBD" }}>Customer deployments include white-label lesson covers, custom exam questions, custom completion certificates.</div>
            </div>
          </div>
          <span
            className="font-mono text-[9px] uppercase tracking-[0.25em] px-2.5 py-1.5 rounded"
            style={{
              background: "#101010",
              color: "#888",
              border: "1px solid #1F1F1F",
            }}
          >
            White-label ready
          </span>
        </div>

        {/* Direct entry to the Training Center — prospects can browse every track without auth in demo mode */}
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <Link
            to="/training"
            className="inline-flex items-center gap-2 h-12 px-5 font-mono text-[11px] uppercase tracking-[0.3em] font-black rounded transition-colors"
            style={{ background: "#FF6A00", color: "#000", border: "1px solid #FF6A00" }}
            data-testid="training-showcase-open-center"
          >
            Open Training Center <ArrowRight className="w-4 h-4" />
          </Link>
          <span className="font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: "#8A8A8A" }}>
            4 tracks · 30+ lessons · EN / ES · QR delivery
          </span>
        </div>
      </div>
    </section>
  );
}

function PathCard({ path }) {
  const Icon = path.icon;
  return (
    <div className="p-5 sm:p-6" style={{ background: "#0F0F0F" }} data-testid={`training-path-${path.tag.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-start gap-3">
          <span className="inline-flex items-center justify-center w-11 h-11 rounded-md shrink-0" style={{ background: `${path.accent}1F`, color: path.accent, border: `1px solid ${path.accent}55` }}>
            <Icon className="w-5 h-5" />
          </span>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em]" style={{ color: path.accent }}>{path.tag}</div>
            <div className="font-display text-lg font-black text-white leading-tight">{path.title}</div>
          </div>
        </div>
        <span className="font-mono text-[9px] uppercase tracking-[0.2em] px-1.5 py-0.5 rounded shrink-0" style={{ background: "#1A1A1A", color: "#BDBDBD", border: "1px solid #2A2A2A" }}>
          {path.chip}
        </span>
      </div>

      <p className="text-xs leading-relaxed mb-4" style={{ color: "#BDBDBD" }}>{path.blurb}</p>

      <div className="space-y-2">
        {path.items.map(([name, pct]) => (
          <div key={name}>
            <div className="flex justify-between mb-1">
              <span className="text-[11px] text-white">{name}</span>
              <span className="font-mono text-[10px]" style={{ color: pct >= 80 ? "#65DA76" : pct >= 60 ? "#FFD000" : "#BDBDBD" }}>
                {pct}%
              </span>
            </div>
            <div className="h-1 rounded-full overflow-hidden" style={{ background: "#1A1A1A" }}>
              <div
                className="h-full transition-all"
                style={{
                  width: `${pct}%`,
                  background: pct >= 80 ? "#2E7D32" : pct >= 60 ? "#FFB000" : path.accent,
                }}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <span className="font-mono text-[9px] uppercase tracking-[0.2em] px-1.5 py-0.5 rounded inline-flex items-center gap-1" style={{ background: "rgba(255,106,0,0.12)", color: "#FF6A00", border: "1px solid rgba(255,106,0,0.3)" }}>
          <Languages className="w-3 h-3" /> EN / ES
        </span>
        <span className="font-mono text-[9px] uppercase tracking-[0.2em] px-1.5 py-0.5 rounded inline-flex items-center gap-1" style={{ background: "#1A1A1A", color: "#FFFFFF", border: "1px solid #2A2A2A" }}>
          <QrCode className="w-3 h-3" /> QR Access
        </span>
        <span className="font-mono text-[9px] uppercase tracking-[0.2em] px-1.5 py-0.5 rounded inline-flex items-center gap-1" style={{ background: "#1A1A1A", color: "#FFFFFF", border: "1px solid #2A2A2A" }}>
          <FileText className="w-3 h-3" /> PDF Cert
        </span>
      </div>
    </div>
  );
}
